import React, { useState } from 'react';
import './BulkDataModal.css';

const BulkDataModal = ({ onClose, onConfirm, dataType }) => {
  const [inputData, setInputData] = useState('');
  const [error, setError] = useState('');
  const [errorLine, setErrorLine] = useState(null);
  const [previewData, setPreviewData] = useState({
    cations: [],
    anions: [],
    neutrals: []
  });

  const validateData = (data) => {
    console.log('Original data:', data);
    
    // First preprocess the entire data to remove spaces
    const preprocessedData = data
      .split('\n')
      .map(line => {
        console.log('Processing line:', line);
        // First, combine any parts that are just (*) with the previous part
        const parts = line.split(/\s+/).filter(Boolean);
        console.log('Initial split parts:', parts);
        
        // Combine parts that are just (*) with the previous part
        const combinedParts = [];
        for (let i = 0; i < parts.length; i++) {
          if (parts[i] === '(*)' && combinedParts.length > 0) {
            combinedParts[combinedParts.length - 1] += '(*)';
          } else {
            combinedParts.push(parts[i]);
          }
        }
        console.log('Combined parts:', combinedParts);

        if (combinedParts.length >= 2) {
          // Remove all spaces and normalize the component part
          const component = combinedParts[0]
            .replace(/\s+/g, '')  // Remove all spaces
            .replace(/\(\s*/g, '(')  // Remove spaces after opening parenthesis
            .replace(/\s*\)/g, ')')  // Remove spaces before closing parenthesis
            .replace(/\s*-\s*/g, '-')  // Remove spaces around minus
            .replace(/\s*\+\s*/g, '+')  // Remove spaces around plus
            .replace(/\s*\*\s*/g, '*');  // Remove spaces around asterisk
          console.log('Processed component:', component);
          return `${component} ${combinedParts[1]}`;
        }
        return line;
      })
      .join('\n');

    console.log('Preprocessed data:', preprocessedData);

    const lines = preprocessedData.split('\n').filter(line => line.trim());
    const preview = {
      cations: [],
      anions: [],
      neutrals: []
    };
    let hasError = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      console.log('Validating line:', line);
      const parts = line.split(/\s+/).filter(Boolean);
      
      if (parts.length < 2) {
        setError(`Line ${i + 1}: Missing amount value`);
        setErrorLine(i);
        hasError = true;
        break;
      }

      const component = parts[0];
      const amount = parts[1];

      if (!component) {
        setError(`Line ${i + 1}: Missing component name`);
        setErrorLine(i);
        hasError = true;
        break;
      }

      if (isNaN(amount)) {
        setError(`Line ${i + 1}: Amount must be a number`);
        setErrorLine(i);
        hasError = true;
        break;
      }

      console.log('Component to classify:', component);
      // Determine which table this component belongs to
      let table = 'neutrals'; // default
      
      // Check for neutral species with charge in parentheses (e.g., N(0))
      if (component.match(/^[A-Z][a-z]?\(0\)$/)) {
        console.log('Matched neutral with (0)');
        table = 'neutrals';
      }
      // Check for cations with charge in parentheses (e.g., Sr(+2), Fe(+3))
      else if (component.match(/^[A-Z][a-z]?\([+]?\d+\)$/)) {
        console.log('Matched cation with (+n)');
        table = 'cations';
      }
      // Check for anions with charge in parentheses (e.g., SO4(-2), CO3(-2))
      else if (component.match(/^[A-Z][a-z]?\d*\(-\d+\)$/)) {
        console.log('Matched anion with (-n)');
        table = 'anions';
      }
      // Check for anions with asterisk (e.g., HCO3-(*))
      else if (component.match(/-\(\*\)$/)) {
        console.log('Matched anion with (*) (robust)');
        table = 'anions';
      }
      // Check for anions with hyphen (e.g., Acetate-)
      else if (component.match(/^[A-Z][a-z]+-$/)) {
        console.log('Matched anion with -');
        table = 'anions';
      }
      // Check for anions with charge number (e.g., SO4-2)
      else if (component.match(/^[A-Z][a-z]?\d*[A-Z][a-z]?\d*-\d+$/)) {
        console.log('Matched anion with -n');
        table = 'anions';
      }
      // Check for simple cations (e.g., Na+, Ca2+)
      else if (component.match(/^[A-Z][a-z]?\d*\+$/)) {
        console.log('Matched cation with +');
        table = 'cations';
      }
      // Check for simple anions (e.g., Cl-, Br-)
      else if (component.match(/^[A-Z][a-z]?\d*\-$/)) {
        console.log('Matched anion with -');
        table = 'anions';
      }
      // Check for polyatomic ions (e.g., SO42-, CO32-)
      else if (component.match(/^[A-Z][a-z]?\d*[A-Z][a-z]?\d*\-$/)) {
        console.log('Matched polyatomic anion');
        table = 'anions';
      }
      // Check for complex ions (e.g., Fe(OH)2+, Al(OH)4-)
      else if (component.match(/^[A-Z][a-z]?\([A-Z][a-z]?\d*\)\d*[+-]$/)) {
        console.log('Matched complex ion');
        if (component.endsWith('+')) {
          table = 'cations';
        } else if (component.endsWith('-')) {
          table = 'anions';
        }
      }
      // Check for anions with asterisk (e.g., HCO3-(*))
      else if (component.match(/^[A-Z][a-z]?\d*[A-Z][a-z]?\d*-\s*\(\*\)$/)) {
        console.log('Matched anion with (*)');
        table = 'anions';
      }
      // Check for anions with asterisk (simplified pattern)
      else if (component.match(/^[A-Z][a-z]?\d*[A-Z][a-z]?\d*-\s*\(\*\)$/)) {
        console.log('Matched anion with (*) - simplified');
        table = 'anions';
      }
      // Everything else goes to neutrals
      else {
        console.log('No match found, defaulting to neutral');
      }

      console.log('Classification result:', component, '→', table);
      preview[table].push({ component, amount });
    }

    if (!hasError) {
      setError('');
      setErrorLine(null);
      setPreviewData(preview);
    }

    return !hasError;
  };

  const handleInputChange = (e) => {
    const newData = e.target.value;
    setInputData(newData);
    validateData(newData);
  };

  const handleSubmit = () => {
    if (validateData(inputData)) {
      onConfirm(previewData);
    }
  };

  const hasData = previewData.cations.length > 0 || 
                 previewData.anions.length > 0 || 
                 previewData.neutrals.length > 0;

  return (
    <div className="bulk-data-modal-overlay">
      <div className="bulk-data-modal">
        <h3>Bulk Data Entry</h3>
        <div className="bulk-data-instructions">
          <p>Enter data in the format: component amount (one per line)</p>
          <p className="bulk-data-instructions-note">
            Components will be automatically sorted into cations (ending with +), anions (ending with -), and neutrals.
          </p>
        </div>
        <div className="bulk-data-textarea-container">
          <textarea
            value={inputData}
            onChange={handleInputChange}
            placeholder="Na+ 1000&#10;Ca2+ 500&#10;Cl- 200&#10;CO2 100"
            rows={10}
            className={error ? 'error' : ''}
          />
          {errorLine !== null && (
            <div 
              className="error-highlight"
              style={{
                top: `${errorLine * 2}rem`,
                height: '1.5em'
              }}
            />
          )}
        </div>
        {error && (
          <div className="bulk-data-error">{error}</div>
        )}
        <div className="bulk-data-preview">
          <h4>Cations</h4>
          <table>
            <thead>
              <tr>
                <th>Component</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {previewData.cations.map((row, index) => (
                <tr key={index}>
                  <td>{row.component}</td>
                  <td>{row.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h4>Anions</h4>
          <table>
            <thead>
              <tr>
                <th>Component</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {previewData.anions.map((row, index) => (
                <tr key={index}>
                  <td>{row.component}</td>
                  <td>{row.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <h4>Neutrals</h4>
          <table>
            <thead>
              <tr>
                <th>Component</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {previewData.neutrals.map((row, index) => (
                <tr key={index}>
                  <td>{row.component}</td>
                  <td>{row.amount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bulk-data-modal-buttons">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
          <button 
            className="next-button" 
            onClick={handleSubmit}
            disabled={!!error || !hasData}
          >
            Add data
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkDataModal; 